/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get as _get, clamp, every, isEmpty, values } from 'lodash';
import { GetterTree } from 'vuex';

import { Audit, AuditError } from '@/pages/audit_setup_wizard/types';
import { Step } from '@/components/step_navigation/StepNavigation.vue';
import { DropdownOption } from '@/components/cards/types';
import { RootState } from '@/state/types';

import { State } from './state';

export type Getters = { [P in keyof GettersDef]: ReturnType<GettersDef[P]> };

class GettersDef {
  // UI getters
  isLoading(state: State): Audit | {} {
    return state.isLoading;
  }
  isSaving(state: State): Audit | {} {
    return state.isSaving;
  }
  controlsDisabled(state: State): Audit | {} {
    return state.isSaving || state.isLoading;
  }
  currentStep(_: State, getters: Getters, rootState: RootState): number {
    const step = parseInt(rootState.route.params.step, 10);
    return clamp(step, 1, getters.getStepInfo.length) || 1;
  }
  hasPreviousStep(_: State, getters: Getters): boolean {
    return getters.currentStep > 1;
  }
  hasNextStep(_: State, getters: Getters): boolean {
    return getters.currentStep < 5;
  }
  getStepInfo(_: State, getters: Getters): Step[] {
    return [
      {
        id: 1,
        title: 'Info',
        done: true,
        disabled: false,
      },
      {
        id: 2,
        title: 'Scope',
        done: false,
        disabled: getters.getAuditId == -1,
      },
      {
        id: 3,
        title: 'Requests',
        done: false,
        disabled: getters.getAuditId == -1,
      },
      {
        id: 4,
        title: 'Assessment',
        done: false,
        disabled: getters.getAuditId == -1,
      },
      {
        id: 5,
        title: 'Review',
        done: false,
        disabled: getters.getAuditId == -1,
      },
    ];
  }
  isRequestMapperVisible(state: State): boolean {
    return state.isRequestMapperVisible;
  }

  // Validation
  isNewAuditValid(state: State, getters: Getters): boolean {
    if (state.currentStep !== 1) {
      return true;
    }

    const hasNoAuditErrors = every(values(state.auditError), isEmpty);

    // Only applies on step 1
    if (getters.currentStep == 1 && state.audit.managedIn.value === 'JIRA') {
      const isJiraDataValid = state.jiraBus.jiraData.isValid;

      return hasNoAuditErrors && isJiraDataValid;
    }

    return hasNoAuditErrors;
  }

  // Audit getters
  isEditingAudit(state: State): boolean {
    return !!state.audit.code;
  }
  getAuditId(state: State, __: Getters, rootState: RootState): number {
    return parseInt(rootState.route.params.auditId, 10) || state.audit.id || -1;
  }
  getAuditStatus(state: State): string {
    return state.audit.status;
  }
  getAudit(state: State): Audit | {} {
    return state.audit;
  }
  getSyncExternalComments(state: State): boolean {
    return state.audit.syncExternalComments;
  }
  getSyncExternalAttachments(state: State): boolean {
    return state.audit.syncExternalAttachments;
  }
  getAuditError(state: State): (field: keyof AuditError) => string | undefined {
    return field => {
      const fieldError = _get(state, ['auditError', field]);
      return fieldError ? fieldError.join(' ') : undefined;
    };
  }
  getAuditTypes(): DropdownOption[] {
    return [
      {
        value: 'internal',
        label: 'Internal',
      },
      {
        value: 'external',
        label: 'External',
      },
    ];
  }
  getStorageTypes(state: State): DropdownOption[] {
    const types = <DropdownOption[]>[
      {
        value: 'ZENGRC',
        label: 'ZenGRC',
      },
    ];

    if (state.jiraEnabled) {
      types.push({
        value: 'JIRA',
        label: 'Jira',
      });
    }
    if (state.servicenowEnabled) {
      types.push({
        value: 'SERVICENOW',
        label: 'ServiceNow',
      });
    }
    return types;
  }
  getManagedIn(state: State): string {
    return state.audit.managedIn.value;
  }
  isManagedInZenGRC(state: State): boolean {
    return state.audit.managedIn.value === 'ZENGRC';
  }
  isManagedInExternalSystem(state: State, getters: Getters): boolean {
    // For now it excludes ServiceNow because only the Jira integration should work with
    // the new flow (as specified for the new Jira Assessments functionality).
    if (state.flagNewTicketing) {
      return !getters.isManagedInZenGRC;
    } else {
      return !getters.isManagedInZenGRC && !getters.isManagedInServiceNow;
    }
  }
  isManagedInServiceNow(state: State): boolean {
    return state.audit.managedIn.value === 'SERVICENOW';
  }
  showNoControlsInScope(state: State, getters: Getters): boolean {
    return getters.canDisplayRequestMapper && !state.audit.mappedControls.length;
  }
  displayRequestMapper(state: State, getters: Getters): boolean {
    return getters.canDisplayRequestMapper && state.audit.mappedControls.length > 0;
  }
  canDisplayRequestMapper(state: State, getters: Getters): boolean {
    return getters.isRequestMapperVisible && (getters.isManagedInZenGRC || getters.isManagedInExternalSystem);
  }
  showMapperButton(state: State, getters: Getters): boolean {
    return getters.currentStep === 3;
  }
  getNumberOfRequests(state: State, getters: Getters): number {
    if (getters.isManagedInZenGRC || getters.isManagedInExternalSystem) {
      return state.audit.mappedRequests.length;
    }

    return state.remoteRequests.length;
  }
  getRemoteRequests(state: State): object[] {
    return state.remoteRequests;
  }

  // JIRA
  getJiraBus(state: State): any {
    return state.jiraBus;
  }

  // TICKETING
  getSelectedTicketing(state: State): any {
    return state.audit.managedIn.label;
  }

  // Assessment template
  getPerformAssessmentOptions(): object[] {
    return [
      {
        text: 'Yes',
        value: true,
      },
      {
        text: 'No',
        value: false,
      },
    ];
  }
  performingAssessment(state: State): boolean {
    return state.performingAssessment;
  }
  getAssessorOptions(state: State): DropdownOption[] {
    return state.templateAssessors;
  }
  getVerifierOptions(state: State): DropdownOption[] {
    return state.templateVerifiers;
  }

  // Storage integration
  isStorageIntegrationEnabled(_: State, getters: Getters): boolean {
    return getters.getAudit.managedIn.value === 'ZENGRC';
  }
}

export default (GettersDef.prototype as any) as GetterTree<State, any>;
